import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { apiDelete, apiGet, apiPost } from '../../../services/api';

export default function CompanyTeamTab({ token }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('manager');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState('');

  const loadMembers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiGet('/api/v1/company/team/members', token);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMembers(Array.isArray(data.data) ? data.data : []);
      } else {
        setMembers([]);
      }
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  async function handleInvite(e) {
    e.preventDefault();
    setInviteSuccess('');
    setInviteError('');
    setRemoveError('');
    setInviting(true);

    try {
      const res = await apiPost(
        '/api/v1/company/team/invite',
        { email: inviteEmail.trim(), role: inviteRole },
        token
      );
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        setInviteSuccess(`Invitation sent to ${inviteEmail.trim()}.`);
        setInviteEmail('');
        setInviteRole('manager');
        await loadMembers();
        return;
      }

      if (res.status === 422) {
        const messages = [];
        if (data.errors) {
          Object.values(data.errors).forEach((errs) => {
            if (Array.isArray(errs)) messages.push(...errs);
            else if (errs) messages.push(String(errs));
          });
        }
        setInviteError(messages.join(' ') || data.message || 'Unable to send invitation.');
        return;
      }

      setInviteError(data.message || 'Unable to send invitation.');
    } catch {
      setInviteError('Unable to send invitation.');
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(memberId) {
    setRemoveError('');
    setInviteSuccess('');
    setRemovingId(memberId);

    try {
      const res = await apiDelete(`/api/v1/company/team/members/${memberId}`, token);
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        await loadMembers();
        return;
      }

      if (res.status === 422 && data.errors) {
        const messages = [];
        Object.values(data.errors).forEach((errs) => {
          if (Array.isArray(errs)) messages.push(...errs);
          else if (errs) messages.push(String(errs));
        });
        setRemoveError(messages.join(' ') || data.message || 'Unable to remove member.');
        return;
      }

      setRemoveError(data.message || 'Unable to remove member.');
    } catch {
      setRemoveError('Unable to remove member.');
    } finally {
      setRemovingId(null);
    }
  }

  function roleBadgeVariant(role) {
    if (role === 'admin') return 'primary';
    if (role === 'manager') return 'info';
    return 'secondary';
  }

  return (
    <div className="rwm-company-team" data-testid="rwm-company-team">
      <h2 className="h5 mb-3">Invite team member</h2>
      <Form onSubmit={handleInvite} className="mb-4" data-testid="rwm-team-invite-form">
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <Form.Group controlId="teamInviteEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                data-testid="rwm-team-invite-email"
              />
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group controlId="teamInviteRole">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="rwm-team-invite-role"
              >
                <option value="manager">Manager</option>
                <option value="viewer">Viewer</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Button
              type="submit"
              variant="info"
              disabled={inviting || !inviteEmail.trim()}
              data-testid="rwm-team-invite-submit"
            >
              {inviting ? 'Sending…' : 'Invite'}
            </Button>
          </div>
        </div>
      </Form>

      {inviteSuccess ? (
        <Alert variant="success" data-testid="rwm-team-invite-success">
          {inviteSuccess}
        </Alert>
      ) : null}
      {inviteError ? (
        <Alert variant="danger" data-testid="rwm-team-invite-error">
          {inviteError}
        </Alert>
      ) : null}
      {removeError ? (
        <Alert variant="danger" data-testid="rwm-team-remove-error">
          {removeError}
        </Alert>
      ) : null}

      <h2 className="h5 mb-3">Team members</h2>
      {loading ? (
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      ) : (
        <div className="table-responsive" data-testid="rwm-team-member-list">
          <Table className="rwm-company-dashboard__table align-middle mb-0">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const isSelf = user?.id === member.id;
                return (
                  <tr key={member.id} data-testid={`rwm-team-member-${member.id}`}>
                    <td>
                      <div>{member.name || member.email}</div>
                      {member.name ? (
                        <div className="small text-muted">{member.email}</div>
                      ) : null}
                    </td>
                    <td>
                      <Badge bg={roleBadgeVariant(member.role)}>{member.role}</Badge>
                    </td>
                    <td>
                      {isSelf ? (
                        <span className="text-muted small">You</span>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={removingId === member.id}
                          onClick={() => handleRemove(member.id)}
                          data-testid={`rwm-team-member-remove-${member.id}`}
                        >
                          {removingId === member.id ? 'Removing…' : 'Remove'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {members.length === 0 ? (
            <p className="text-muted mt-3 mb-0">No team members yet.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
